package com.appsyde.shared.modules.ticket.data.mapper

import com.appsyde.shared.modules.ticket.data.parseInstantOrNull
import com.appsyde.shared.modules.ticket.data.dto.TicketDraftDto
import com.appsyde.shared.modules.ticket.data.dto.TicketDto
import com.appsyde.shared.modules.ticket.data.dto.TicketHistoryItemDto
import com.appsyde.shared.modules.ticket.data.dto.TicketOriginalHistoryItemDto
import com.appsyde.shared.modules.ticket.data.dto.TicketRespondDto
import com.appsyde.shared.modules.ticket.data.dto.TicketRespondMessageDto
import com.appsyde.shared.modules.ticket.domain.model.Direction
import com.appsyde.shared.modules.ticket.domain.model.AssignedTo
import com.appsyde.shared.modules.ticket.domain.model.HistoryMeta
import com.appsyde.shared.modules.ticket.domain.model.Ticket
import com.appsyde.shared.modules.ticket.domain.model.WriteLock
import com.appsyde.shared.modules.ticket.domain.model.TicketDraft
import com.appsyde.shared.modules.ticket.domain.model.TicketHistoryItem
import com.appsyde.shared.modules.ticket.domain.model.TicketOriginalHistoryItem
import com.appsyde.shared.modules.ticket.domain.model.MessageAttachment
import com.appsyde.shared.modules.ticket.domain.model.TicketRespondMessage

fun TicketDto.toDomain(): Ticket = Ticket(
    ticketId = id,
    providerThreadId = threadId,
    status = status,
    needsResponse = needsResponse,
    isRead = isRead,
    stateSummary = stateSummary,
    respondContext = respondContext,
    detectedMemoryJson = detectedMemoryJson,
    missingKeysJson = missingKeysJson,
    toConfirmKeysJson = toConfirmKeysJson,
    currentIntent = currentIntent,
    currentPriority = currentPriority,
    currentComplexity = currentComplexity,
    currentSuggestedActionCode = currentSuggestedActionCode,
    currentSuggestedActionLabel = currentSuggestedActionLabel,
    clientEmail = clientEmail,
    subject = subject,
    updatedAtMs = updatedAtMs,
    lastInboundSentAtMs = lastInboundSentAtMs,
    lastOutboundSentAtMs = lastOutboundSentAtMs,
    lastInboundMessageId = lastInboundMessageId,
    extractStatus = extractStatus,
    extractJson = extractJson,
    assignedTo = assignedTo?.let { AssignedTo(accountUserId = it.accountUserId, displayName = it.displayName) },
    writeLock = writeLock?.let { w -> parseInstantOrNull(w.expiresAt)?.let { exp -> WriteLock(accountUserId = w.accountUserId, displayName = w.displayName, expiresAt = exp) } }
)

fun TicketRespondDto.toDomain(): Ticket = Ticket(
    ticketId = id,
    providerThreadId = threadId,
    status = status,
    needsResponse = needsResponse,
    stateSummary = stateSummary,
    respondContext = respondContext,
    detectedMemoryJson = detectedMemoryJson,
    missingKeysJson = missingKeysJson,
    toConfirmKeysJson = toConfirmKeysJson,
    clientEmail = clientEmail,
    subject = subject,
    currentIntent = currentIntent,
    currentPriority = currentPriority,
    currentComplexity = currentComplexity,
    currentSuggestedActionCode = currentSuggestedActionCode,
    currentSuggestedActionLabel = currentSuggestedActionLabel,
    currentSuggestedActionWhy = currentSuggestedActionWhy,
    detectedSourcesJson = detectedSourcesJson,
    currentDraftSuggestion = currentDraftSuggestion,
    currentDraftSuggestionUpdatedAtMs = currentDraftSuggestionUpdatedAtMs,
    currentDraftSourceMessageId = currentDraftSourceMessageId,
    updatedAtMs = updatedAtMs,
    lastInboundSentAtMs = lastInboundSentAtMs,
    lastOutboundSentAtMs = null,
    lastInboundMessageId = null,
    historyMeta = if (hasHistory) HistoryMeta(hasHistory = true, exchangesCount = historyCount) else null,
    assignedTo = assignedTo?.let { AssignedTo(accountUserId = it.accountUserId, displayName = it.displayName) },
    writeLock = writeLock?.let { w -> parseInstantOrNull(w.expiresAt)?.let { exp -> WriteLock(accountUserId = w.accountUserId, displayName = w.displayName, expiresAt = exp) } }
)

fun TicketRespondMessageDto.toDomain(): TicketRespondMessage = TicketRespondMessage(
    messageId = id,
    direction = when {
        direction.equals("INBOUND", ignoreCase = true) -> Direction.INBOUND
        direction.equals("OUTBOUND", ignoreCase = true) -> Direction.OUTBOUND
        else -> Direction.UNKNOWN
    },
    sentAtMs = sentAtMs,
    summary = summary,
    bodyPreview = bodyPreview,
    authorEmail = authorEmail,
    hasAttachments = hasAttachments,
    originalBody = originalBody,
    attachments = attachments.map { MessageAttachment(id = it.id, filename = it.filename, mimeType = it.mimeType, sizeBytes = it.sizeBytes) }
)

fun TicketDraftDto.toDomain(): TicketDraft = TicketDraft(
    status = status,
    body = body,
    updatedAtMs = updatedAtMs
)

fun TicketHistoryItemDto.toDomain(): TicketHistoryItem = TicketHistoryItem(
    id = id,
    direction = direction,
    sentAtMs = sentAtMs,
    summary = summary
)

fun TicketOriginalHistoryItemDto.toDomain(): TicketOriginalHistoryItem = TicketOriginalHistoryItem(
    id = id,
    direction = direction,
    sentAtMs = sentAtMs,
    providerMessageId = providerMessageId,
    from = from,
    to = to,
    subject = subject,
    mimeType = mimeType,
    body = body,
    snippet = snippet,
    error = error
)
